import React, {useEffect, useState} from "react";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import UNINITIALISED from "utils/uninitialised";
import {
  Workflow,
  WorkflowContext,
  WorkflowId,
  WorkflowInput,
} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";
import {Issue, IssueId} from "common/types/issue";
import {Project} from "common/types/project";
import WorkflowSelector from "./workflow_selector";
import workflowFetchAction from "modules/flowmaster/actions/workflow_fetch";
import contextsFetchAction from "modules/flowmaster/actions/contexts_fetch";
import requestor from "requestor";
import WorkflowInputs from "./workflow_inputs";
import {isEmpty} from "underscore";

function getDefaultValueForInput(input) {
  return input.has_default ? input.defaultValue : "";
}

function getInitialValueForWorkflow(inputs) {
  const initialValueObject = inputs.reduce((acc, input) => {
    acc[input.id] = getDefaultValueForInput(input);
    return acc;
  }, {});
  return initialValueObject;
}

export interface OverrideableFieldData {
  props: {
    value: string | number;
    [key: string]: string | number;
  };
  clearProps: {
    onClick: () => void;
    disabled: boolean;
  };
}

interface WorkflowsState {
  workflows: Workflow[] | typeof UNINITIALISED;
  workflow: Workflow | typeof UNINITIALISED;
  contexts: WorkflowContext[] | typeof UNINITIALISED;
}

interface WorkflowEditorProps {
  issueId: IssueId;
  organisationId: OrganisationId;
  llmAnalysisType: Issue["llm_analysis_type"];
  workflows: Workflow[];
  contexts: WorkflowContext[];
  fetchContexts: (organisationId: OrganisationId) => void;
  project: Project;
  getWorkflow: (
    organisationId: OrganisationId,
    workflowId: WorkflowId,
  ) => {value: {inputs: WorkflowInput[]}};
  selectedWorkflowId: WorkflowId;
  llm_settings: Issue["llm_settings"];
  updateLlmSettings: (settings: Partial<Issue["llm_settings"]>) => void;
  workflow: Workflow;
  isOverrideMode: boolean;
  getOverridableFieldData: (
    fieldType: string,
    key: string,
    fieldMethods: Record<string, unknown>,
    emptyValue: unknown,
    useProposedChanges: boolean,
  ) => OverrideableFieldData;
  isDisplayedInPlaybook?: boolean;
  renderOverridenIssuesets?: (
    fieldName: string,
    props?: unknown,
    handleHoverValue?: unknown,
  ) => React.ReactNode;
}

function getWorkflowContextIds(llmAnalysisType, isMatterProject) {
  if (llmAnalysisType === "data_consolidation") {
    return [8];
  }
  if (isMatterProject) {
    return [7, 10, 11];
  }
  if (llmAnalysisType === "data_extraction") {
    return [5];
  }
  return [1];
}

const WorkflowEditor = ({
  issueId,
  organisationId,
  project,
  llmAnalysisType,
  workflows,
  contexts,
  fetchContexts,
  getWorkflow,
  llm_settings,
  updateLlmSettings,
  workflow,
  isOverrideMode,
  getOverridableFieldData,
  isDisplayedInPlaybook = false,
  renderOverridenIssuesets,
}: WorkflowEditorProps) => {
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(
    llm_settings.workflow_settings?.selected_workflow ?? -1,
  );

  useEffect(() => {
    fetchContexts(organisationId);
  }, [organisationId]);

  useEffect(() => {
    const selectedWorkflow = llm_settings.workflow_settings?.selected_workflow;
    if (selectedWorkflow && selectedWorkflow > 0) {
      setSelectedWorkflowId(selectedWorkflow);
    }
  }, [llm_settings]);

  useEffect(() => {
    async function fetchAndUpdateWorkflow() {
      let updatedSettingsForSelectedWorkflow: Partial<
        Issue["llm_settings"]
      > = {};

      // always update selected_workflow when selectedWorkflowId changes
      if (
        selectedWorkflowId > -1 &&
        selectedWorkflowId !==
          llm_settings?.workflow_settings?.selected_workflow
      ) {
        updatedSettingsForSelectedWorkflow = {
          ...llm_settings,
          workflow_settings: {
            ...llm_settings.workflow_settings,
            selected_workflow: selectedWorkflowId,
          },
        };
      }

      if (selectedWorkflowId !== -1) {
        try {
          const response = await getWorkflow(
            organisationId,
            selectedWorkflowId,
          );
          const value = response.value;

          // check if we need to fetch workflow config data
          if (!llm_settings?.workflow_settings?.config?.[selectedWorkflowId]) {
            const workflowData = value.inputs
              ? getInitialValueForWorkflow(value.inputs.values)
              : [];

            // update workflow config data
            const updatedSettingsForConfig = {
              ...updatedSettingsForSelectedWorkflow,
              workflow_settings: {
                ...updatedSettingsForSelectedWorkflow.workflow_settings,
                config: {
                  ...llm_settings.workflow_settings?.config,
                  [selectedWorkflowId]: {
                    inputs: {
                      values: workflowData,
                    },
                  },
                },
              },
            };
            updateLlmSettings(updatedSettingsForConfig);
          } else if (!isEmpty(updatedSettingsForSelectedWorkflow)) {
            updateLlmSettings(updatedSettingsForSelectedWorkflow);
          }
        } catch (error) {
          throw new Error(error);
        }
      }
    }

    fetchAndUpdateWorkflow();
  }, [selectedWorkflowId]);

  const updateWorkflowSettings = updatedInputs => {
    const currentWorkflowSettings =
      llm_settings.workflow_settings?.config?.[selectedWorkflowId];

    const updatedWorkflowSettings = {
      ...currentWorkflowSettings,
      inputs: {
        ...currentWorkflowSettings?.inputs,
        values: updatedInputs,
      },
    };

    const updatedLLMSettings = {
      ...llm_settings,
      workflow_settings: {
        ...llm_settings.workflow_settings,
        config: {
          ...llm_settings?.workflow_settings?.config,
          [selectedWorkflowId]: updatedWorkflowSettings,
        },
      },
    };

    updateLlmSettings(updatedLLMSettings);
  };

  const workflowInputs =
    llm_settings.workflow_settings?.config?.[selectedWorkflowId]?.inputs
      ?.values ?? {};

  const workflowContextIds = getWorkflowContextIds(
    llmAnalysisType,
    project?.top_level_items === "matter",
  );

  return (
    <>
      {!isDisplayedInPlaybook && (
        <div
          style={{
            padding: "0em 2em 1em 2em",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <WorkflowSelector
            organisationId={organisationId}
            contextTypeIds={workflowContextIds}
            contexts={contexts}
            selectedWorkflowId={selectedWorkflowId}
            updateselectedWorkflowId={setSelectedWorkflowId}
            workflows={workflows}
            showStarredWorkflows={true}
          />
        </div>
      )}
      <div
        style={{
          padding: "0em 2em 1em 2em",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <WorkflowInputs
          key={issueId}
          organisationId={organisationId}
          workflows={workflows}
          selectedWorkflow={workflow}
          inputs={workflowInputs}
          updateWorkflowSettings={updateWorkflowSettings}
          selectedWorkflowId={selectedWorkflowId}
          workflowSettings={llm_settings.workflow_settings}
          isOverrideMode={isOverrideMode}
          getOverridableFieldData={getOverridableFieldData}
          contextTypeId={workflow ? workflow.context_id : workflowContextIds[0]}
          isDisplayedInPlaybook={isDisplayedInPlaybook}
          renderOverridenIssuesets={renderOverridenIssuesets}
        />
      </div>
    </>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        getWorkflow: workflowFetchAction(requestor),
        fetchContexts: contextsFetchAction(requestor),
      },
      dispatch,
    ),
  };
}

const mapStateToProps = (state: WorkflowsState) => ({
  workflows: state.workflows === UNINITIALISED ? [] : state.workflows,
  workflow: state.workflow === UNINITIALISED ? null : state.workflow,
  contexts: state.contexts === UNINITIALISED ? [] : state.contexts,
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowEditor);
